/**
 * FundingCalendarView · Nueva vista Calendario 2027 · trimestres con
 * tarjetas por convocatoria y barra de ventana. Diseño validado en el
 * mockup /mockup-funding.html.
 *
 * Props:
 *   calls[]         array de saved_calls con phase, state_2027, priority,
 *                   country_scope, amount_reference, open_window, etc.
 *   onSelect(call)  callback al pinchar una tarjeta
 *   selectedId      id de la tarjeta actualmente resaltada
 */
import { useMemo, useState } from "react";

const PHASE_META = {
  idea:         { label: "💡 Idea",         cls: "bg-amber-100 text-amber-900 border-amber-300" },
  desarrollo:   { label: "🎬 Desarrollo",   cls: "bg-blue-100 text-blue-900 border-blue-300" },
  produccion:   { label: "🎥 Producción",   cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  distribucion: { label: "📢 Distribución", cls: "bg-purple-100 text-purple-900 border-purple-300" },
  festivales:   { label: "🎞 Festivales",   cls: "bg-pink-100 text-pink-900 border-pink-300" },
};

const STATE_META = {
  confirmado:     { label: "CONFIRMADO 2027", cls: "bg-emerald-600 text-white" },
  prevision:      { label: "PREVISIÓN 2027",  cls: "bg-amber-500 text-white" },
  por_determinar: { label: "POR DETERMINAR",  cls: "bg-stone-500 text-white" },
};

const PRIO_META = {
  alta:       { label: "Prioridad alta",       cls: "bg-red-100 text-red-800 border-red-300" },
  media:      { label: "Prioridad media",      cls: "bg-amber-100 text-amber-800 border-amber-300" },
  baja:       { label: "Prioridad baja",       cls: "bg-stone-100 text-stone-600 border-stone-300" },
  especifica: { label: "Prioridad específica", cls: "bg-purple-100 text-purple-800 border-purple-300" },
};

const COUNTRY_LABEL = {
  españa: "España", madrid: "Madrid", ue: "Europa",
  iberoamerica: "Iberoamérica", internacional: "Internacional",
};

const QUARTERS = [
  { key: "Q1", label: "Q1 · Enero – Marzo",        startMonth: 1,  months: ["Enero", "Febrero", "Marzo"] },
  { key: "Q2", label: "Q2 · Abril – Junio",        startMonth: 4,  months: ["Abril", "Mayo", "Junio"] },
  { key: "Q3", label: "Q3 · Julio – Septiembre",   startMonth: 7,  months: ["Julio", "Agosto", "Septiembre"] },
  { key: "Q4", label: "Q4 · Octubre – Diciembre",  startMonth: 10, months: ["Octubre", "Noviembre", "Diciembre"] },
];

function urgencyClassFromDays(days) {
  if (days === null || days === undefined) return "bg-stone-400";
  if (days < 0) return "bg-stone-400"; // cerrada
  if (days < 15) return "bg-red-600";
  if (days < 30) return "bg-amber-500";
  return "bg-emerald-600";
}

function daysUntilIso(iso) {
  if (!iso) return null;
  const target = new Date(iso + "T00:00:00Z");
  if (isNaN(target)) return null;
  const now = new Date();
  return Math.floor((target - now) / (1000 * 60 * 60 * 24));
}

function fmtRangeShort(open_window) {
  if (!open_window) return "";
  const [sM, sD] = open_window.start.split("-");
  const [eM, eD] = open_window.end.split("-");
  const months = ["", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${parseInt(sD, 10)} ${months[parseInt(sM, 10)]} → ${parseInt(eD, 10)} ${months[parseInt(eM, 10)]}`;
}

/** Devuelve {leftPct, widthPct} para pintar la ventana dentro del quarter. */
function windowInQuarter(open_window, quarter) {
  if (!open_window) return null;
  const [sM, sD] = open_window.start.split("-").map(Number);
  const [eM, eD] = open_window.end.split("-").map(Number);
  const qStart = quarter.startMonth;
  const qEnd = quarter.startMonth + 2;
  // Si la ventana ni siquiera toca este quarter, no pintar.
  if (eM < qStart || sM > qEnd) return null;
  // Calcular posición como fracción de los 90 días del trimestre (aprox).
  const totalDays = 91;
  const dayInQ = (m, d) => {
    if (m < qStart) return 0;
    if (m > qEnd) return totalDays;
    // días acumulados desde inicio del trimestre
    const dim = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let acc = 0;
    for (let mm = qStart; mm < m; mm++) acc += dim[mm];
    return Math.min(totalDays, acc + d);
  };
  const startDay = dayInQ(sM, sD);
  const endDay = dayInQ(eM, eD);
  const leftPct = (startDay / totalDays) * 100;
  const widthPct = Math.max(3, ((endDay - startDay) / totalDays) * 100);
  return { leftPct, widthPct };
}

export default function FundingCalendarView({ calls, onSelect, selectedId }) {
  const [quarterIdx, setQuarterIdx] = useState(() => {
    const m = new Date().getMonth(); // 0..11
    return Math.floor(m / 3);
  });
  const q = QUARTERS[quarterIdx];

  // Filtrar convocatorias que solapan con el trimestre actual
  const visible = useMemo(() => {
    return (calls || []).filter(c => c.open_window && windowInQuarter(c.open_window, q));
  }, [calls, q]);

  return (
    <div className="space-y-4" data-testid="funding-calendar-view">
      {/* Nav trimestre */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-stone-900">{q.label} 2027</h2>
        <div className="flex gap-1">
          <button
            onClick={() => setQuarterIdx((i) => Math.max(0, i - 1))}
            disabled={quarterIdx === 0}
            data-testid="funding-cal-prev"
            className="rounded-full border border-stone-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest hover:bg-stone-50 disabled:opacity-40">
            ← {quarterIdx > 0 ? QUARTERS[quarterIdx - 1].key : "Q1"}
          </button>
          <button
            onClick={() => {
              const m = new Date().getMonth();
              setQuarterIdx(Math.floor(m / 3));
            }}
            data-testid="funding-cal-today"
            className="rounded-full border border-stone-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest hover:bg-stone-50">
            Hoy
          </button>
          <button
            onClick={() => setQuarterIdx((i) => Math.min(3, i + 1))}
            disabled={quarterIdx === 3}
            data-testid="funding-cal-next"
            className="rounded-full border border-stone-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest hover:bg-stone-50 disabled:opacity-40">
            {quarterIdx < 3 ? QUARTERS[quarterIdx + 1].key : "Q4"} →
          </button>
        </div>
      </div>

      {/* Cabecera de meses (referencia visual del timeline) */}
      <div className="rounded-t-xl border border-b-0 border-stone-200 bg-stone-50 px-5 py-2">
        <div className="grid grid-cols-3 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
          {q.months.map((m) => <div key={m}>{m}</div>)}
        </div>
      </div>

      {/* Lista de tarjetas */}
      <div className="-mt-4 space-y-3">
        {visible.length === 0 && (
          <div className="rounded-xl border border-stone-200 bg-white p-8 text-center text-stone-500">
            No hay convocatorias abiertas en {q.label}.
          </div>
        )}
        {visible.map((c) => {
          const phase = PHASE_META[c.phase] || PHASE_META.desarrollo;
          const state = STATE_META[c.state_2027] || STATE_META.por_determinar;
          const prio = PRIO_META[c.priority] || PRIO_META.baja;
          const win = windowInQuarter(c.open_window, q);
          const days = daysUntilIso(c.opens_at);
          const isSelected = selectedId === c.id;
          const daysLabel =
            days === null ? "" :
            days < 0 ? `Cerró hace ${-days} días` :
            days === 0 ? "Abre hoy" :
            `Abre en ${days} días`;
          const daysColor =
            days === null ? "text-stone-500" :
            days < 0 ? "text-stone-500" :
            days < 15 ? "text-red-700" :
            days < 30 ? "text-amber-700" :
            "text-emerald-700";
          const barCls = urgencyClassFromDays(days);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect?.(c)}
              data-testid={`funding-cal-card-${c.id}`}
              className={`w-full rounded-xl border bg-white p-4 shadow-sm hover:shadow-md text-left transition ${
                isSelected ? "border-2 border-[#941620] bg-red-50/40 shadow-md" : "border-stone-200 hover:border-stone-400"
              }`}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${phase.cls}`}>{phase.label}</span>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${state.cls}`}>{state.label}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${prio.cls}`}>{prio.label}</span>
                <span className={`ml-auto text-[11px] font-semibold ${daysColor}`}>{daysLabel}</span>
              </div>
              <h3 className="font-display text-lg leading-tight text-stone-900">{c.title}</h3>
              <p className="mt-0.5 text-[11px] text-stone-500">
                {c.organism}
                {c.country_scope ? ` · ${COUNTRY_LABEL[c.country_scope] || c.country_scope}` : ""}
                {c.amount ? ` · ${c.amount}` : ""}
              </p>
              {/* Barra de ventana */}
              {win && (
                <div className="mt-3 relative h-8 rounded-md bg-stone-100">
                  <div
                    className={`absolute top-0 h-full rounded flex items-center px-2 ${barCls}`}
                    style={{ left: `${win.leftPct}%`, width: `${win.widthPct}%` }}
                  >
                    <span className="whitespace-nowrap text-[10px] font-bold text-white">
                      {fmtRangeShort(c.open_window)}
                    </span>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Panel lateral con la ficha del `call` seleccionado. Se muestra fixed en el layout padre. */
export function FundingCallDetailPanel({ call, projects = [], onStatusChange, onPresent, onEdit }) {
  if (!call) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 bg-white p-6 text-center text-stone-500" data-testid="funding-detail-empty">
        <p className="text-sm">Selecciona una convocatoria del calendario</p>
        <p className="mt-1 text-[11px]">Verás aquí su ficha completa con notas prácticas y acciones.</p>
      </div>
    );
  }
  const phase = PHASE_META[call.phase] || PHASE_META.desarrollo;
  const state = STATE_META[call.state_2027] || STATE_META.por_determinar;
  const prio = PRIO_META[call.priority] || PRIO_META.baja;
  const days = daysUntilIso(call.opens_at);
  const daysLabel =
    days === null ? "—" :
    days < 0 ? `Cerró hace ${-days} días` :
    days === 0 ? "Abre hoy" :
    `~${days} días`;
  const daysColor =
    days === null ? "text-stone-600" :
    days < 0 ? "text-stone-600" :
    days < 15 ? "text-red-700" :
    days < 30 ? "text-amber-700" :
    "text-emerald-700";

  return (
    <aside className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="funding-detail-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${phase.cls}`}>{phase.label}</span>
        <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${state.cls}`}>{state.label}</span>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${prio.cls}`}>{prio.label}</span>
      </div>
      <h3 className="font-display text-xl leading-tight text-stone-900">{call.title}</h3>
      <p className="mt-1 text-[11px] text-stone-500">
        {call.organism}
        {call.country_scope ? ` · ${COUNTRY_LABEL[call.country_scope] || call.country_scope}` : ""}
      </p>

      <dl className="mt-4 space-y-2 border-t border-stone-100 pt-3 text-[13px]">
        <div className="flex justify-between gap-2">
          <dt className="text-stone-500">Ventana 2027</dt>
          <dd className="font-semibold text-right">{fmtRangeShort(call.open_window)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-stone-500">Días para abrir</dt>
          <dd className={`font-semibold ${daysColor}`}>{daysLabel}</dd>
        </div>
        {call.amount && (
          <div className="flex justify-between gap-2">
            <dt className="text-stone-500">Cuantía</dt>
            <dd className="text-right font-semibold">{call.amount}</dd>
          </div>
        )}
        {call.amount_reference && call.amount_reference !== call.amount && (
          <div className="flex justify-between gap-2">
            <dt className="text-stone-500">Dotación / detalle</dt>
            <dd className="text-right text-[12px]">{call.amount_reference}</dd>
          </div>
        )}
      </dl>

      {call.summary && (
        <div className="mt-4 border-t border-stone-100 pt-3">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-stone-500">Descripción</p>
          <p className="text-[13px] leading-relaxed text-stone-700">{call.summary}</p>
        </div>
      )}
      {call.requirements && (
        <div className="mt-3">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-stone-500">Notas prácticas</p>
          <p className="text-[13px] leading-relaxed text-stone-700">{call.requirements}</p>
        </div>
      )}

      <div className="mt-4 border-t border-stone-100 pt-3">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-stone-500">Estado en Grabarte</p>
        <select
          value={call.status || "Por revisar"}
          onChange={(e) => onStatusChange?.(call, e.target.value)}
          data-testid="funding-detail-status"
          className="w-full rounded-full border border-amber-300 bg-amber-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-amber-900"
        >
          {["Por revisar", "Interesa", "Preparando", "Presentada", "Aprobada", "Denegada"].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 flex gap-2">
        <button onClick={() => onPresent?.(call)}
                data-testid="funding-detail-present"
                className="flex-1 rounded-full bg-[#941620] px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#7a1119]">
          📝 Presentar
        </button>
        {call.url && (
          <a href={call.url} target="_blank" rel="noopener noreferrer"
             data-testid="funding-detail-website"
             className="rounded-full border border-stone-300 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:bg-stone-50">
            Web ↗
          </a>
        )}
        {onEdit && (
          <button onClick={() => onEdit?.(call)}
                  data-testid="funding-detail-edit"
                  className="rounded-full border border-stone-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:bg-stone-50">
            ✎
          </button>
        )}
      </div>
    </aside>
  );
}

export { PHASE_META, STATE_META, PRIO_META, COUNTRY_LABEL };
